import type { ReactNode } from "react";

/**
 * A fold with a proper handle: a circled chevron that turns when open, a
 * label that reads as a control, and a left rule on what unfolds. Two
 * looks: "row" sits inside a card on its own line; "card" is a bordered
 * panel of its own, for a fold that stands alone in a form.
 */
export function Disclosure({
  summary,
  children,
  open,
  look = "row",
  className = "",
  contentClassName = "",
}: {
  summary: ReactNode;
  children: ReactNode;
  /** Initial state. A details element is the visitor's to toggle after. */
  open?: boolean;
  look?: "row" | "card";
  className?: string;
  contentClassName?: string;
}) {
  const shell =
    look === "card"
      ? "rounded-2xl border border-hairline bg-card px-5 py-1 transition-colors open:border-hairline-strong"
      : "";
  return (
    <details className={`group ${shell} ${className}`} open={open}>
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-3 text-sm font-semibold text-ink select-none [&::-webkit-details-marker]:hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-deep">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-hairline-strong bg-card text-ink-soft transition group-hover:border-ink group-hover:text-ink group-open:rotate-90 group-open:border-magenta group-open:text-magenta"
        >
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 3.5 10.5 8 6 12.5" />
          </svg>
        </span>
        <span className="group-hover:text-magenta">{summary}</span>
      </summary>
      <div
        className={`mb-3 ml-3.5 border-l border-hairline pl-6 ${contentClassName}`}
      >
        {children}
      </div>
    </details>
  );
}
